/**
 * End the session and land on /login exactly ONCE.
 *
 * THE BUG. Three separate places reacted to a dead token by clearing storage
 * and assigning `window.location.href = '/login'` — fetch-wrapper's
 * "Unauthenticated" branch and two paths in axios. None of them checked whether
 * a redirect was already under way, and none checked whether we were already ON
 * /login.
 *
 * That is fine for one request and visibly broken for many. A page like the
 * dashboard has a dozen calls in flight; when the token dies mid-flight (an
 * admin editing the user deletes their tokens — see
 * InternalUsersController@update) EVERY one of them comes back 401 and EVERY
 * one assigns location.href. Each assignment restarts a navigation the browser
 * had already begun, so the page appears to load over and over. The axios
 * IDLE_TIMEOUT path makes it worse by firing its redirect on a 2s timer, which
 * lands after the others and reloads once more.
 *
 * The latch is module-level, which is the right scope: it lives exactly as long
 * as the document that is being torn down, and the fresh document that follows
 * starts with it clear.
 *
 * `replace` rather than `href` so the dead page does not sit in history — with
 * `href`, Back returns the user to a screen whose every request 401s, which
 * starts the whole thing again.
 */

import { safeClearLocalStorage } from '@/utils/safeClearStorage';

const LOGIN_PATH = '/login';

/** Set for the lifetime of this document once a redirect has been committed. */
let redirecting = false;

/**
 * How we actually leave the page. Indirected because jsdom refuses to let a
 * test stub `window.location.replace` (the property is non-configurable), and
 * deleting `window.location` outright takes localStorage with it — storage is
 * bound to the document origin. Production always uses the default.
 */
let navigate: (url: string) => void = (url) => window.location.replace(url);

/** Test seam. */
export function resetSessionRedirect(navigator?: (url: string) => void): void {
    redirecting = false;
    navigate = navigator || ((url) => window.location.replace(url));
}

/** Test seam — has a redirect already been committed? */
export function isRedirectingToLogin(): boolean {
    return redirecting;
}

/**
 * Wipe every trace of the session. Deliberately explicit about the known keys
 * BEFORE the bulk clear: safeClearLocalStorage preserves `costings-backup:*` on
 * purpose, and the named removals document what actually matters here.
 */
function clearSession(): void {
    try {
        [
            'user',
            'csrfToken',
            'projectselected',
            'weektimesheet',
            'projecttimesheet',
            '_secure__ls__metadata',
            'security_logs',
            'modernize_v3_template'
        ].forEach((key) => localStorage.removeItem(key));

        safeClearLocalStorage();
        sessionStorage.clear();
    } catch {
        // Private-mode / quota failures must not stop the redirect.
    }
}

/**
 * Clear the session and go to /login — at most once per document.
 *
 * @param delayMs wait before navigating, so a caller can show a toast first.
 *        The latch is taken IMMEDIATELY regardless, so a delayed redirect still
 *        suppresses every other caller in the meantime.
 */
export function redirectToLogin(delayMs = 0): void {
    if (redirecting) return;
    redirecting = true;

    // Already on the login page: clear up, but navigating again would only
    // reload it — which is the flicker this function exists to stop.
    if (typeof window !== 'undefined' && window.location?.pathname === LOGIN_PATH) {
        clearSession();
        return;
    }

    clearSession();

    if (delayMs > 0) {
        setTimeout(() => navigate(LOGIN_PATH), delayMs);
        return;
    }

    navigate(LOGIN_PATH);
}
